export interface Price {
  price: string
  regular_price: string
  sale_price: string
  price_range: null | any
  currency_code: string
  currency_symbol: string
  currency_minor_unit: number
  currency_decimal_separator: string
  currency_thousand_separator: string
  currency_prefix: string
  currency_suffix: string
}

export interface AddToCart {
  text: string
  description: string
  url: string
  minimum: number
  maximum: number
  multiple_of: number
}

export interface Address {
  first_name: string
  last_name: string
  company: string
  address_1: string
  address_2: string
  city: string
  state: string
  postcode: string
  country: string
  phone: string
  email?: string
}

export interface Total {
  total_items: string
  total_items_tax: string
  total_fees: string
  total_fees_tax: string
  total_discount: string
  total_discount_tax: string
  total_shipping: null | string
  total_shipping_tax: null | string
  total_price: string
  total_tax: string
  tax_lines: any[]
  currency_code: string
  currency_symbol: string
  currency_minor_unit: number
  currency_decimal_separator: string
  currency_thousand_separator: string
  currency_prefix: string
  currency_suffix: string
}

export interface Cart {
  coupons: any[]
  shipping_rates: any[]
  shipping_address: Address
  billing_address: Address
  items: CartItem[]
  items_count: number
  items_weight: number
  cross_sells: any[]
  needs_payment: boolean
  needs_shipping: boolean
  has_calculated_shipping: boolean
  fees: any[]
  totals: Total
  errors: any[]
  payment_requirements: string[]
  extensions: any
}

export interface Prices {
  price: string
  regular_price: string
  sale_price: string
  price_range: null | string
  currency_code: string
  currency_symbol: string
  currency_minor_unit: number
  currency_decimal_separator: string
  currency_thousand_separator: string
  currency_prefix: string
  currency_suffix: string
  raw_prices: {
    precision: number
    price: string
    regular_price: string
    sale_price: string
  }
}

export interface Totals {
  line_subtotal: string
  line_subtotal_tax: string
  line_total: string
  line_total_tax: string
  currency_code: string
  currency_symbol: string
  currency_minor_unit: number
  currency_decimal_separator: string
  currency_thousand_separator: string
  currency_prefix: string
  currency_suffix: string
}

export interface CartItem {
  key: string
  id: number
  quantity: number
  quantity_limits: {
    minimum: number
    maximum: number
    multiple_of: number
    editable: boolean
  }
  name: string
  short_description: string
  description: string
  sku: string
  low_stock_remaining: null | number
  backorders_allowed: boolean
  show_backorder_badge: boolean
  sold_individually: boolean
  permalink: string
  images: Image[]
  variation: any[]
  item_data: any[]
  prices: Prices
  totals: Totals
  catalog_visibility: string
  extensions: any
}

export interface Styles {
  [key: string]: React.CSSProperties
}

export interface Link {
  href: string
}

export interface Links {
  self: Link[]
  collection: Link[]
}

export interface ProductCategory {
  id: number
  name: string
  slug: string
  parent: number
  description: string
  display: string
  image: null
  menu_order: number
  count: number
  _links: Links
}

export interface Image {
  id: number
  date_created: string
  date_created_gmt: string
  date_modified: string
  date_modified_gmt: string
  src: string
  name: string
  alt: string
}

export interface Tag {
  id: number
  name: string
  slug: string
}

interface Category {
  id: number
  name: string
  slug: string
}

export interface MetaData {
  id: number
  key: string
  value: string | string[] | { [key: string]: string }
}

export interface Dimensions {
  length: string
  width: string
  height: string
}

export interface Product {
  id: number
  name: string
  slug: string
  permalink: string
  date_created: string
  date_created_gmt: string
  date_modified: string
  date_modified_gmt: string
  type: string
  status: string
  featured: boolean
  catalog_visibility: string
  description: string
  short_description: string
  sku: string
  price: string
  regular_price: string
  sale_price: string
  date_on_sale_from: null | string
  date_on_sale_from_gmt: null | string
  date_on_sale_to: null | string
  date_on_sale_to_gmt: null | string
  on_sale: boolean
  purchasable: boolean
  total_sales: number
  virtual: boolean
  downloadable: boolean
  downloads: any[]
  download_limit: number
  download_expiry: number
  external_url: string
  button_text: string
  tax_status: string
  tax_class: string
  manage_stock: boolean
  stock_quantity: null | number
  backorders: string
  backorders_allowed: boolean
  backordered: boolean
  low_stock_amount: null | number
  sold_individually: boolean
  weight: string
  dimensions: Dimensions
  shipping_required: boolean
  shipping_taxable: boolean
  shipping_class: string
  shipping_class_id: number
  reviews_allowed: boolean
  average_rating: string
  rating_count: number
  upsell_ids: number[]
  cross_sell_ids: number[]
  parent_id: number
  purchase_note: string
  categories: Category[]
  tags: Tag[]
  images: Image[]
  attributes: any[]
  default_attributes: any[]
  variations: any[]
  grouped_products: any[]
  menu_order: number
  price_html: string
  related_ids: number[]
  meta_data: MetaData[]
  stock_status: string
  has_options: boolean
  _links: {
    self: { href: string }[]
    collection: { href: string }[]
  }
}

export interface Delivery {
  type: string
  fetch_id: string
  recipient: string
  address_lat: string
  address_lng: string
  address: string
  address1: string
  city: string
  postal_code: string
  date: string
  invoice_number: string
  sender: string
  group_name: string
  status: string
  eta_time: null | string
  job_time: null | string
  job_sequence: null | string
  leave_at_door_ordered: boolean
  live_eta: null | string
  notify_email: string
  phone_number: string
  tracking_link: string
  tracking_status: string
  tracking_status_code: string
  time_window: null | string
  instructions: string
  instructions_saved_at: string
  arrived_address: null | string
  arrived_at: null | string
  pod_address: string
  pod_at: null | string
  formatted_date: string
  eta: string
  pod_at_fi: null | string
  arrived_at_fi: null | string
  ordered_services: string
  full_address: string
  additional_services: string
  first_name: string
  last_name: string
  email: string
  failed: boolean
  final: boolean
}

export interface ProductTag {
  id: number
  name: string
  slug: string
  description: string
  count: number
}

export interface LocationLinkInfo {
  href: string
}

export interface PostalAddress {
  id: number
  postal_code: string
  post_office_name: string
  municipality_name: string
  is_collection_zone: boolean
  is_delivery_zone: boolean
}

// Typescript interfaces
export interface UpdateCustomerAddress {
  first_name: string
  last_name: string
  company: string
  address_1: string
  address_2: string
  city: string
  state: string
  postcode: string
  country: string
  email: string
  phone: string
}

export interface UpdateCustomerRequestBody {
  billing_address: UpdateCustomerAddress
  shipping_address: UpdateCustomerAddress
}

export interface CreateOrderRequestBody {
  payment_method: string
  payment_method_title: string
  set_paid: boolean
  billing: BillingDetails
  shipping: ShippingDetails
  line_items: LineItem[]
  shipping_lines: ShippingLine[]
  customer_note?: string
}

export interface BillingDetails {
  first_name: string
  last_name: string
  address_1: string
  address_2?: string
  city: string
  state: string
  postcode: string
  country: string
  email: string
  phone: string
}

export interface ShippingDetails extends BillingDetails {}

export interface LineItem {
  product_id: number
  quantity: number
  variation_id?: number
}

interface ShippingLine {
  method_id: string
  method_title: string
  total: string
}

export enum CompanyType {
  Hok = 'hok',
  FoodoraMarket = 'foodora-market',
  Ruokaboksi = 'ruokaboksi',
}
